<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :style="{ width: '48rem', borderRadius: '12px' }"
    @show="fetchDetails"
  >
    <template #header>
      <div class="flex w-full items-center justify-between pr-2">
        <div class="flex items-center gap-3">
          <span
            class="bg-navy-700 flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full text-xs font-bold tracking-wide text-white"
          >
            {{ initials }}
          </span>
          <div>
            <p class="text-navy-800 text-sm font-semibold">
              {{ participantName }}
            </p>
            <p v-if="submittedLabel" class="text-navy-400 text-xs">
              Submitted {{ submittedLabel }}
            </p>
          </div>
        </div>
        <StatusBadge v-if="verification" :variant="statusDisplay.variant">
          {{ statusDisplay.label }}
        </StatusBadge>
      </div>
    </template>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <i class="pi pi-spinner pi-spin text-navy-400 text-2xl" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <i class="pi pi-exclamation-circle mb-3 text-2xl text-red-400" />
      <p class="text-navy-600 text-sm">Failed to load verification details</p>
    </div>

    <!-- Content -->
    <div v-else-if="verification" class="space-y-5">
      <!-- Photo grid -->
      <div class="grid grid-cols-2 gap-4">
        <!-- ID Photo card -->
        <div class="overflow-hidden rounded-lg border border-neutral-200">
          <div
            class="flex items-center justify-between border-b border-neutral-200 bg-neutral-100 px-4 py-2.5"
          >
            <span
              class="text-navy-600 flex items-center gap-2 text-xs font-semibold"
            >
              <i class="pi pi-id-card text-navy-400 text-sm" />
              Photo ID
            </span>
            <button
              v-if="verification.idPhotoUrl"
              class="text-navy-400 hover:text-navy-600 cursor-pointer text-xs transition-colors"
              @click="openImage(verification.idPhotoUrl!)"
            >
              <i class="pi pi-expand text-sm" />
            </button>
          </div>
          <div
            class="flex min-h-[200px] items-center justify-center bg-neutral-50 p-2"
          >
            <img
              v-if="verification.idPhotoUrl"
              :src="verification.idPhotoUrl"
              alt="Photo ID"
              class="max-h-[320px] w-full object-contain"
            />
            <div
              v-else
              class="flex flex-col items-center justify-center gap-2 text-neutral-300"
            >
              <i class="pi pi-image text-4xl" />
              <span class="text-xs">No photo uploaded</span>
            </div>
          </div>
        </div>

        <!-- Selfie card -->
        <div class="overflow-hidden rounded-lg border border-neutral-200">
          <div
            class="flex items-center justify-between border-b border-neutral-200 bg-neutral-100 px-4 py-2.5"
          >
            <span
              class="text-navy-600 flex items-center gap-2 text-xs font-semibold"
            >
              <i class="pi pi-user text-navy-400 text-sm" />
              Selfie
            </span>
            <button
              v-if="verification.selfieUrl"
              class="text-navy-400 hover:text-navy-600 cursor-pointer text-xs transition-colors"
              @click="openImage(verification.selfieUrl!)"
            >
              <i class="pi pi-expand text-sm" />
            </button>
          </div>
          <div
            class="flex min-h-[200px] items-center justify-center bg-neutral-50 p-2"
          >
            <img
              v-if="verification.selfieUrl"
              :src="verification.selfieUrl"
              alt="Selfie"
              class="max-h-[320px] w-full object-contain"
            />
            <div
              v-else
              class="flex flex-col items-center justify-center gap-2 text-neutral-300"
            >
              <i class="pi pi-image text-4xl" />
              <span class="text-xs">No photo uploaded</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Capture details -->
      <div class="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <p
          class="text-navy-500 mb-3 text-[11px] font-semibold tracking-wide uppercase"
        >
          Capture Details
        </p>
        <div class="grid grid-cols-2 gap-x-6 gap-y-3">
          <div v-if="hasGps">
            <p class="text-navy-400 text-[11px] font-medium">GPS Coordinates</p>
            <p class="text-navy-700 text-sm font-medium">
              {{ verification.latitude }}, {{ verification.longitude }}
            </p>
          </div>
          <div v-if="verification.submittedAt">
            <p class="text-navy-400 text-[11px] font-medium">Submitted</p>
            <p class="text-navy-700 text-sm font-medium">
              {{ formatDate(verification.submittedAt) }}
            </p>
          </div>
          <div>
            <p class="text-navy-400 text-[11px] font-medium">Session Created</p>
            <p class="text-navy-700 text-sm font-medium">
              {{ formatDate(verification.createdAt) }}
            </p>
          </div>
          <div>
            <p class="text-navy-400 text-[11px] font-medium">Status</p>
            <StatusBadge :variant="statusDisplay.variant">
              {{ statusDisplay.label }}
            </StatusBadge>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <AppButton variant="secondary" @click="dialogVisible = false">
          Done
        </AppButton>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "primevue/dialog"
import { computed, ref } from "vue"

import type { ParticipantVerificationType } from "../../pages/Order/types"
import { apolloClient } from "../../queries/client"
import VERIFICATION_DETAILS from "../../queries/participant_verification/show.gql"
import AppButton from "../ui/AppButton.vue"
import StatusBadge from "../ui/StatusBadge.vue"

interface Props {
  participantName: string
  orderId: string
  participantId: string | null
}

const props = defineProps<Props>()

const dialogVisible = defineModel<boolean>("visible", { default: false })

const loading = ref(false)
const error = ref(false)
const verification = ref<ParticipantVerificationType | null>(null)

type BadgeVariant = "success" | "warning" | "error" | "info" | "neutral"

const statusDisplay = computed<{ label: string; variant: BadgeVariant }>(() => {
  if (!verification.value) return { label: "Unknown", variant: "neutral" }
  const map: Record<string, { label: string; variant: BadgeVariant }> = {
    SUBMITTED: { label: "Pending Review", variant: "warning" },
    APPROVED: { label: "Approved", variant: "success" },
    REJECTED: { label: "Rejected", variant: "error" },
  }
  return (
    map[verification.value.status] ?? { label: "Unknown", variant: "neutral" }
  )
})

const initials = computed(() =>
  props.participantName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase(),
)

const hasGps = computed(
  () =>
    verification.value?.latitude != null &&
    verification.value?.longitude != null,
)

const submittedLabel = computed(() => {
  if (!verification.value?.submittedAt) return null
  return formatDate(verification.value.submittedAt)
})

const formatDate = (iso: string): string => {
  const date = new Date(iso)
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const openImage = (url: string) => {
  window.open(url, "_blank")
}

const fetchDetails = async () => {
  if (!props.participantId) return

  loading.value = true
  error.value = false
  verification.value = null

  try {
    const result = await apolloClient.query({
      query: VERIFICATION_DETAILS,
      variables: { orderId: props.orderId },
      fetchPolicy: "network-only",
    })

    const participants = result.data?.order?.participants?.nodes ?? []
    const participant = participants.find(
      (p: { id: string }) => p.id === props.participantId,
    )
    const verifications = participant?.participantVerifications ?? []
    verification.value =
      verifications.length > 0 ? verifications[verifications.length - 1] : null
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}
</script>
